import type { Measurement } from './api';
import type { Network } from './operators';

export type NetworkSummary = { network: Network; count: number; bestRsrp?: number; bestSinr?: number; bestDown?: number; best: Measurement };

const maxOf = (xs: (number | undefined)[]) => {
  const v = xs.filter((x): x is number => typeof x === 'number');
  return v.length ? Math.max(...v) : undefined;
};

/** Higher is better: SINR first (quality decides FWA speed), RSRP as tie-break; missing values rank last. */
function better(a: Measurement, b: Measurement): number {
  const sa = a.sinr ?? -Infinity;
  const sb = b.sinr ?? -Infinity;
  if (sa !== sb) return sb - sa;
  return (b.rsrp ?? -Infinity) - (a.rsrp ?? -Infinity);
}

export function compareByNetwork(ms: Measurement[]): NetworkSummary[] {
  const by = new Map<Network, Measurement[]>();
  for (const m of ms) {
    if (!by.has(m.network)) by.set(m.network, []);
    by.get(m.network)!.push(m);
  }
  const out: NetworkSummary[] = [];
  for (const [network, list] of by) {
    const best = [...list].sort(better)[0];
    out.push({
      network,
      count: list.length,
      bestRsrp: maxOf(list.map(x => x.rsrp)),
      bestSinr: maxOf(list.map(x => x.sinr)),
      bestDown: maxOf(list.map(x => x.downMbps)),
      best,
    });
  }
  return out.sort((a, b) => better(a.best, b.best));
}
